// covered-by: frontends/carrie/src/ask-v2/review/__tests__/useAskPunttReview.test.tsx
import {
  type AskPunttReviewCommentWriteBody,
  type GetAskPunttReviewResponse,
} from "@mg/schemas/src/christo/askPunttReview";
import {
  type UseMutationResult,
  useMutation,
  useQuery,
  useQueryClient,
} from "@tanstack/react-query";
import { useCallback } from "react";

import {
  createAskPunttReviewComment,
  getAskPunttReview,
  reopenAskPunttReviewResolved,
  setAskPunttReviewChangeReviewed,
  updateAskPunttReviewComment,
} from "../services/reviews";

export const askV2ReviewKeys = {
  all: ["ask-v2-review"] as const,
  detail: (reviewId: string) =>
    [...askV2ReviewKeys.all, "detail", reviewId] as const,
};

/** Poll while AI is still writing comments so list-card badges do not stick at 0. */
export function askPunttReviewRefetchIntervalMs(
  review: { aiRunning?: boolean } | undefined,
): number | false {
  if (review?.aiRunning === true) return 5_000;
  return false;
}

export function useAskPunttReview(
  reviewId: string | null | undefined,
  options?: { enabled?: boolean },
) {
  const enabled = (options?.enabled ?? true) && !!reviewId;
  return useQuery({
    queryKey: askV2ReviewKeys.detail(reviewId ?? ""),
    queryFn: () => getAskPunttReview(reviewId!),
    enabled,
    // why: cards mount while AI comments are still landing — keep the summary
    // fresh until aiRunning clears so the badge does not stick at 0
    refetchInterval: (query) =>
      askPunttReviewRefetchIntervalMs(query.state.data?.review),
  });
}

// why: react-query hands a still-pending mutation the options from the LATEST
// render, so any `reviewId` read from the hook's closure -- in mutationFn,
// onMutate or onSuccess -- is whatever the pane shows at that moment, not the
// review the reader acted on. Neither review page keys this pane on the review
// id, so picking another version re-renders the same hook with a new id. The
// target therefore rides in the mutation variables, bound when the call is
// made; variables are fixed for the life of the mutation.
function useBoundToReview<TVars, TData, TContext>(
  reviewId: string | null | undefined,
  mutation: UseMutationResult<
    TData,
    Error,
    { targetReviewId: string | null; vars: TVars },
    TContext
  >,
) {
  const { mutate: rawMutate, mutateAsync: rawMutateAsync } = mutation;
  const targetReviewId = reviewId ?? null;
  const mutate = useCallback(
    (vars: TVars) => rawMutate({ targetReviewId, vars }),
    [rawMutate, targetReviewId],
  );
  const mutateAsync = useCallback(
    (vars: TVars) => rawMutateAsync({ targetReviewId, vars }),
    [rawMutateAsync, targetReviewId],
  );
  return { ...mutation, mutate, mutateAsync };
}

export function useCreateAskPunttReviewComment(
  reviewId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      targetReviewId,
      vars: body,
    }: {
      targetReviewId: string | null;
      vars: AskPunttReviewCommentWriteBody;
    }) => {
      if (!targetReviewId) {
        throw new Error("A reviewId is required to add a review comment");
      }
      return createAskPunttReviewComment(targetReviewId, body);
    },
    // A POST that landed after a version switch used to append version A's
    // comment to version B's cached review: a phantom finding and artwork pin
    // on a version nobody wrote it to, plus B's human count off by one.
    //
    // This create-path target is its own defect, independent of the
    // update-path cache work in useUpdateAskPunttReviewComment below: removing
    // either one leaves the other's race live.
    onSuccess: ({ comment }, { targetReviewId }) => {
      if (!targetReviewId) return;
      const key = askV2ReviewKeys.detail(targetReviewId);
      queryClient.setQueryData<GetAskPunttReviewResponse>(key, (prev) => {
        if (!prev) return prev;
        if (prev.review.comments.some((c) => c._id === comment._id)) {
          return prev;
        }
        return {
          ...prev,
          review: {
            ...prev.review,
            comments: [...prev.review.comments, comment],
            commentsSummary: {
              ...prev.review.commentsSummary,
              human: prev.review.commentsSummary.human + 1,
            },
          },
        };
      });
      void queryClient.invalidateQueries({ queryKey: key });
    },
  });
  return useBoundToReview(reviewId, mutation);
}

export function useUpdateAskPunttReviewComment(
  reviewId: string | null | undefined,
) {
  const queryClient = useQueryClient();
  const mutation = useMutation({
    mutationFn: ({
      targetReviewId,
      vars: { commentId, body },
    }: {
      targetReviewId: string | null;
      vars: {
        commentId: string;
        body: Partial<AskPunttReviewCommentWriteBody>;
      };
    }) => {
      if (!targetReviewId) {
        throw new Error("A reviewId is required to update a review comment");
      }
      return updateAskPunttReviewComment(targetReviewId, commentId, body);
    },
    // why: this mutation patches the cache instead of refetching, so a review
    // GET that was already in flight when the judgment lands resolves with the
    // pre-judgment snapshot and overwrites the patch below -- a rejected
    // finding pops back into the findings list and the pins. Cancel the read
    // first, the same way useSubmitAskPunttFeedback guards its cache write.
    //
    // The target comes from the variables (see useBoundToReview): onMutate
    // awaits the cancel before the PUT is sent, and a version switch during
    // that await used to send the judgment to the switched-to version and
    // cancel THAT version's read -- a cancelled read is not retried, so the
    // new version sat empty.
    //
    // This cancel and the one in onSuccess cover different reads, so neither
    // is redundant: this one kills a read that opened BEFORE the judgment, so
    // it cannot resolve while the PUT is still open and overwrite an EARLIER
    // judgment -- the onSuccess patch only rewrites the comment this PUT
    // judged, so it would not restore the one the snapshot reverted. The
    // onSuccess cancel covers reads that open after the PUT started.
    onMutate: async ({ targetReviewId }) => {
      if (targetReviewId) {
        await queryClient.cancelQueries({
          queryKey: askV2ReviewKeys.detail(targetReviewId),
        });
      }
    },
    onSuccess: async ({ comment }, { targetReviewId }) => {
      if (!targetReviewId) return;
      const key = askV2ReviewKeys.detail(targetReviewId);
      // why: onMutate only cancels the reads that were already open when the
      // judgment started. A read that STARTS while the PUT is in flight -- a
      // window-focus refetch, or the invalidate useCreateAskPunttReviewComment
      // fires when a human comment is added -- still reads christo's
      // pre-judgment snapshot and lands after the patch below, resurrecting
      // the rejected finding in the list and on the artwork pins. Cancel again
      // here so the patch is the last write for that read window.
      await queryClient.cancelQueries({ queryKey: key });
      queryClient.setQueryData<GetAskPunttReviewResponse>(key, (prev) => {
        if (!prev) return prev;
        const hasComment = prev.review.comments.some(
          (item) => item._id === comment._id,
        );
        if (!hasComment) return prev;
        return {
          ...prev,
          review: {
            ...prev.review,
            comments: prev.review.comments.map((item) =>
              item._id === comment._id ? comment : item,
            ),
          },
        };
      });
    },
  });
  return useBoundToReview(reviewId, mutation);
}

/**
 * Marks a changed window reviewed on the review of the newer version, and puts
 * christo's answer -- every mark on that review -- into its cached read, so the
 * checkbox does not wait for a refetch.
 */
export function useSetAskPunttReviewChangeReviewed() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reviewId,
      comparisonId,
      number,
      reviewed,
    }: {
      reviewId: string;
      comparisonId: string;
      number: number;
      reviewed: boolean;
    }) =>
      setAskPunttReviewChangeReviewed(reviewId, comparisonId, number, reviewed),
    onSuccess: ({ reviewedChanges }, { reviewId }) => {
      queryClient.setQueryData<GetAskPunttReviewResponse>(
        askV2ReviewKeys.detail(reviewId),
        (prev) =>
          prev
            ? { ...prev, review: { ...prev.review, reviewedChanges } }
            : prev,
      );
    },
  });
}

/**
 * Reopens a finding a newer version fixed. christo answers with the whole
 * review -- the fixed list with its stamp and the comment it added -- and that
 * replaces the cached read.
 */
export function useReopenAskPunttReviewResolved() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({
      reviewId,
      commentId,
      reopened,
    }: {
      reviewId: string;
      commentId: string;
      reopened: boolean;
    }) => reopenAskPunttReviewResolved(reviewId, commentId, reopened),
    onSuccess: ({ review }, { reviewId }) => {
      queryClient.setQueryData<GetAskPunttReviewResponse>(
        askV2ReviewKeys.detail(reviewId),
        (prev) => (prev ? { ...prev, review } : prev),
      );
    },
  });
}
